"use client";

import Link from "next/link";
import { Heart, Search, ShoppingCart, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import styles from "./Header.module.css";

const themes = {
  ent: {
    logo: "/assets/Icons/ent-logo.png",
    logoAlt: "ENT Central Logo",
  },
  cosmetic: {
    logo: "/assets/Icons/cosmetic-central-logo.png",
    logoAlt: "Cosmetic Central Logo",
  },
};

const navItems = [
  {
    label: "ENDOLIFTX",
    href: "/endoliftx",
  },
  {
    label: "COSMETIC CENTRAL",
    href: "/cosmetic-central",
    dropdown: [
      { label: "Weight Loss & Wellness", href: "/weight-loss-and-wellness" },
      { label: "ENDOLIFTX\u00ae", href: "/endoliftx" },
      { label: "Cellular Rejuvenation Treatment", href: "/cellular-rejuvenation-treatment" },
      { label: "Jalupro\u00ae Glow Peel", href: "/jalupro-glow-peel" },
    ],
  },
  {
    label: "LADYLIFT\u00ae",
    href: "/ladylift",
  },
  {
    label: "SERVICES",
    href: "/services",
    dropdown: [
      { label: "General & Paediatric ENT", href: "/general-and-paediatric-ent" },
      { label: "Head & Neck", href: "/head-and-neck" },
      { label: "Robotic Head & Neck", href: "/robotic-head-neck" },
      { label: "Thyroid", href: "/thyroid" },
      { label: "Parathyroid", href: "/parathyroid" },
      { label: "Voice & Swallowing Problems", href: "/voice-and-swallowing-problems" },
      { label: "Salivary Problems & Sialendoscopy", href: "/salivary-problems-and-sialendoscopy" },
      { label: "Head & Neck Skin Cancer", href: "/head-and-neck-skin-cancer" },
      { label: "Advanced Facial Plastics with Rhinoplasty", href: "/advanced-facial-plastics-with-rhinoplasty" },
      { label: "Cosmetic Surgery & Aesthetics", href: "/cosmetic-surgery-and-aesthetics" },
      { label: "Advanced Rhinology", href: "/advanced-rhinology" },
      { label: "Endoscopic Ear Surgery & Otology", href: "/endoscopic-ear-surgery-and-otology" },
      { label: "Snoring & Sleep Apnoea", href: "/snoring-and-sleep-apnoea" },
    ],
  },
  {
    label: "ABOUT DR NAVEED",
    href: "/about-dr-naveed",
    dropdown: [{ label: "Specialities", href: "/specialities" }],
  },
  {
    label: "OUR CLINIC",
    href: "/our-clinic",
    // dropdown: [
    //   { label: "Clinic Overview", href: "/clinic" },
    //   { label: "Location", href: "/location" },
    //   { label: "Contact Us", href: "/contact" },
    // ],
  },
  {
    label: "SHOP NOW",
    href: "/products",
  },
];

export default function Header({
  theme = "ent",
  onAccountClick,
  isAuthenticated = false,
}) {
  const pathname = usePathname() || "/";
  const [hash, setHash] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const themeClass = styles[theme] ?? "";
  const shouldCollapseActions =
    (theme === "ent" && isAuthenticated) || theme === "cosmetic";

  useEffect(() => {
    const updateHash = () => setHash(window.location.hash || "");

    updateHash();
    window.addEventListener("hashchange", updateHash);

    return () => window.removeEventListener("hashchange", updateHash);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenMobileDropdown(null);
    setAccountMenuOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    if (!menuOpen) {
      setOpenMobileDropdown(null);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!accountMenuOpen) return;

    const handlePointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [accountMenuOpen]);

  const isHrefActive = (href) => {
    const [hrefPath, hrefHash] = href.split("#");
    const normalizedHrefPath = hrefPath || "/";

    if (hrefHash) {
      return pathname === normalizedHrefPath && hash === `#${hrefHash}`;
    }

    return pathname === normalizedHrefPath;
  };

  const isItemActive = (item) =>
    isHrefActive(item.href) || item.dropdown?.some((dropdownItem) => isHrefActive(dropdownItem.href));

  return (
    <header className={`${styles.header} ${themeClass}`}>
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <span className={styles.topBarText}>
            ENT Central, 187 Broadway Avenue, Palmerston North - NOW OPEN!
          </span>
          <div className={styles.topBarContacts}>
            <a href="tel:0800368326" className={styles.topBarLink}>
              📞 0800 ENT DOC (368 362)
            </a>
            <a href="mailto:admin@entcentral.co.nz" className={styles.topBarLink}>
              📧 admin@entcentral.co.nz
            </a>
          </div>
        </div>
      </div>

      <div className={styles.navBar}>
        <div className={styles.navInner}>
          <div className={styles.logoGroup} aria-label="ENT Central and Cosmetic Central">
            <Link href="/" className={styles.logo}>
              <img src={themes.ent.logo} alt={themes.ent.logoAlt} className={styles.logoImage} />
            </Link>
            <Link href="/cosmetic-central" className={styles.logo}>
              <img
                src={themes.cosmetic.logo}
                alt={themes.cosmetic.logoAlt}
                className={`${styles.logoImage} ${styles.cosmeticLogoImage}`}
              />
            </Link>
          </div>

          <button
            className={`${styles.mobileToggle} ${menuOpen ? styles.mobileToggleOpen : ""}`}
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="site-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

          <nav
            id="site-navigation"
            className={`${styles.navLinks} ${menuOpen ? styles.navLinksOpen : ""}`}
            onClick={(event) => {
              if (event.target.closest("a")) {
                setMenuOpen(false);
              }
            }}
          >
            {navItems.map((item) => {
              const active = isItemActive(item);
              const mobileDropdownOpen = openMobileDropdown === item.label;

              return (
                <div
                  key={item.label}
                  className={`${styles.navItem} ${active ? styles.navItemActive : ""} ${mobileDropdownOpen ? styles.navItemMobileDropdownOpen : ""}`}
                >
                  <Link
                    href={item.href}
                    className={`${styles.navLink} ${item.dropdown ? styles.navLinkWithDropdown : ""} ${active ? styles.navLinkActive : ""}`}
                    aria-haspopup={item.dropdown ? "true" : undefined}
                    aria-expanded={item.dropdown ? mobileDropdownOpen : undefined}
                    aria-current={active ? "page" : undefined}
                    onClick={(event) => {
                      if (!item.dropdown) return;

                      const isMobileNav =
                        typeof window !== "undefined" &&
                        window.matchMedia("(max-width: 900px)").matches;

                      if (!isMobileNav) return;

                      event.preventDefault();
                      event.stopPropagation();
                      setOpenMobileDropdown((currentLabel) =>
                        currentLabel === item.label ? null : item.label
                      );
                    }}
                  >
                    <span>{item.label}</span>
                    {item.dropdown ? <span className={styles.chevron} aria-hidden="true" /> : null}
                  </Link>

                  {item.dropdown ? (
                    <div className={`${styles.dropdown} ${mobileDropdownOpen ? styles.dropdownMobileOpen : ""}`}>
                      {item.dropdown.map((dropdownItem) => {
                        const dropdownActive = isHrefActive(dropdownItem.href);

                        return (
                          <Link
                            key={dropdownItem.label}
                            href={dropdownItem.href}
                            className={`${styles.dropdownLink} ${dropdownActive ? styles.dropdownLinkActive : ""}`}
                            aria-current={dropdownActive ? "page" : undefined}
                          >
                            {dropdownItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>

          <div className={styles.navActions}>
            {shouldCollapseActions ? (
              <div className={styles.accountMenuRoot} ref={accountMenuRef}>
                <button
                  type="button"
                  className={styles.actionLink}
                  aria-label="Account menu"
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  onClick={() => setAccountMenuOpen((open) => !open)}
                >
                  <User size={18} strokeWidth={2.4} />
                </button>

                {accountMenuOpen ? (
                  <div className={styles.accountDropdown} role="menu">
                    <Link href="/products" className={styles.accountDropdownItem} role="menuitem">
                      <Search size={16} strokeWidth={2.2} />
                      <span>Search</span>
                    </Link>
                    <Link href="/wishlist" className={styles.accountDropdownItem} role="menuitem">
                      <Heart size={16} strokeWidth={2.2} />
                      <span>Wishlist</span>
                    </Link>
                    <Link href="/cart" className={styles.accountDropdownItem} role="menuitem">
                      <ShoppingCart size={16} strokeWidth={2.2} />
                      <span>Cart</span>
                    </Link>
                    <button
                      type="button"
                      className={styles.accountDropdownItem}
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        if (onAccountClick) {
                          onAccountClick();
                          return;
                        }

                        window.location.href = "/user/account";
                      }}
                    >
                      <User size={16} strokeWidth={2.2} />
                      <span>Account</span>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <Link href="/products" className={styles.actionLink} aria-label="Search">
                  <Search size={18} strokeWidth={2.4} />
                </Link>
                <Link href="/wishlist" className={styles.actionLink} aria-label="Wishlist">
                  <Heart size={18} strokeWidth={2.4} />
                </Link>
                <Link href="/cart" className={styles.actionLink} aria-label="Cart">
                  <ShoppingCart size={18} strokeWidth={2.4} />
                </Link>
                <button
                  type="button"
                  className={styles.actionLink}
                  aria-label="Account"
                  onClick={() => {
                    if (onAccountClick) {
                      onAccountClick();
                      return;
                    }

                    window.location.href = "/user/account";
                  }}
                >
                  <User size={18} strokeWidth={2.4} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
